import { useEffect, useState } from "react";
import { Printer, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, fmtDate, qty, rp } from "@/lib/api";
import { EmptyState, Loading, PageHeader, SectionTitle, StatCard, StatusBadge } from "@/components/common";
import { TxRow } from "@/components/History";

function Summary({ r }) {
  const items = [["Total Uang Masuk", r.total_masuk, "#1f7a46", "report-in"], ["Total Uang Keluar", r.total_keluar, "#c0392b", "report-out"],
    ["Perubahan Uang Kas", r.cash_change, "#135c37", "report-change"], ["Uang Kas Awal", r.opening.cash, "#135c37", "report-opening"],
    ["Uang Kas Akhir", r.closing.cash, "#135c37", "report-closing"], ["Uang Kas Setelah Kasbon", r.closing.after_credit, "#b7861b", "report-after-credit"],
    ["Sisa Kasbon Pelanggan", r.closing.cust_credit, "#6d4fd8", "report-cust-credit"], ["Sisa Kasbon Pabrik", r.closing.sup_credit, "#2357c9", "report-sup-credit"]];
  return <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">{items.map(([l, v, c, id]) => <StatCard key={id} label={l} value={rp(v)} color={c} testid={id} />)}</div>;
}

const Table = ({ head, rows, testid }) => (
  <div className="overflow-x-auto -mx-1" data-testid={testid}>
    <table className="w-full text-sm min-w-[320px]">
      <thead><tr className="text-left text-xs text-muted-foreground border-b">{head.map((h, i) => <th key={h} className={`py-2 px-1 font-semibold ${i ? "text-right" : ""}`}>{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-b border-[var(--jt-gold)]/10">{r.map((c, j) => <td key={j} className={`py-2 px-1 ${j ? "text-right tabular-nums" : "font-semibold"}`}>{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

export default function ReportView({ title, subtitle, icon, range, picker, showTransactions }) {
  const { ws, version } = useAuth();
  const mats = Object.fromEntries(ws.materials.map((m) => [m.key, m]));
  const [r, setR] = useState(null);
  const [dl, setDl] = useState(false);
  const [start, end] = range;
  useEffect(() => { setR(null); api.get("/report", { params: { start, end } }).then(({ data }) => setR(data)).catch((e) => { toast.error(errMsg(e)); setR(false); }); }, [start, end, version]);
  const download = async () => {
    setDl(true);
    try {
      const res = await api.get("/report/csv", { params: { start, end }, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `laporan-juragan-tahu-${start}-${end}.csv`; a.click(); URL.revokeObjectURL(url);
      toast.success("File CSV berhasil diunduh. Bisa dibuka di Excel.");
    } catch (e) { toast.error("Gagal mengunduh laporan. Silakan coba lagi."); } finally { setDl(false); }
  };
  return (
    <div data-testid="report-page">
      <PageHeader icon={icon} color="#b7861b" title={title} subtitle={subtitle}>
        <div className="no-print flex flex-wrap gap-2">
          {picker}
          <button className="jt-btn jt-btn-ghost" onClick={() => window.print()} disabled={!r} data-testid="report-print-button"><Printer className="w-4 h-4" /> Cetak</button>
          <button className="jt-btn jt-btn-primary" onClick={download} disabled={!r || dl} data-testid="report-csv-button">{dl ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Unduh CSV/Excel</button>
        </div>
      </PageHeader>
      <p className="text-sm text-muted-foreground mb-4" data-testid="report-range">Periode: <b className="text-foreground">{fmtDate(start)} – {fmtDate(end)}</b>{ws.simulation_mode ? " · berisi data contoh (simulasi)" : ""}</p>
      {r === null ? <Loading /> : r === false ? <EmptyState text="Laporan gagal dimuat." /> : (
        <div className="space-y-6">
          <Summary r={r} />
          <div className="grid xl:grid-cols-2 gap-6">
            <div className="jt-card p-5 sm:p-6">
              <SectionTitle>Rincian Harian</SectionTitle>
              <div className="h-48 mb-3 no-print">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={r.daily.map((d) => ({ ...d, label: fmtDate(d.date, { day: "2-digit", month: "2-digit" }) }))}>
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" /><Tooltip formatter={(v) => rp(v)} />
                    <Bar dataKey="masuk" name="Masuk" fill="#2f8a4a" radius={[4, 4, 0, 0]} /><Bar dataKey="keluar" name="Keluar" fill="#d9a21b" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <Table testid="report-daily-table" head={["Tanggal", "Masuk", "Keluar"]} rows={r.daily.filter((d) => d.masuk || d.keluar).map((d) => [fmtDate(d.date), rp(d.masuk), rp(d.keluar)])} />
            </div>
            <div className="space-y-6">
              <div className="jt-card p-5 sm:p-6">
                <SectionTitle>Pengeluaran per Kategori</SectionTitle>
                {r.categories.length === 0 ? <EmptyState text="Tidak ada pengeluaran pada periode ini." /> : <Table testid="report-category-table" head={["Kategori", "Jumlah"]} rows={r.categories.map((c) => [c.category, rp(c.amount)])} />}
              </div>
              <div className="jt-card p-5 sm:p-6">
                <SectionTitle>Bahan Baku</SectionTitle>
                <Table testid="report-material-table" head={["Bahan", "Masuk", "Dipakai", "Stok Akhir"]} rows={r.materials.map((m) => [m.name, `${qty(m.received)} ${m.unit}`, `${qty(m.used)} ${m.unit}`, <span key="s" className="inline-flex items-center gap-2 justify-end">{qty(m.ending)} {m.unit} <StatusBadge status={m.status} /></span>])} />
              </div>
            </div>
          </div>
          {showTransactions && (
            <div className="jt-card p-5 sm:p-6">
              <SectionTitle>Rincian Transaksi ({r.transactions.length})</SectionTitle>
              {r.transactions.length === 0 ? <EmptyState text="Tidak ada transaksi pada bulan ini." /> :
                <div className="divide-y divide-[var(--jt-gold)]/15" data-testid="report-transactions">{r.transactions.map((t) => <TxRow key={t.id} t={t} mats={mats} testid="report-tx-row" />)}</div>}
            </div>
          )}
          <p className="text-xs text-muted-foreground">Catatan: perubahan Uang Kas adalah selisih uang masuk dan keluar, bukan laba bersih.</p>
        </div>
      )}
    </div>
  );
}
